import { toast } from "sonner";
import { Pencil, Mail, Phone, Download } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";

const INQUIRY_LABELS = {
  "consultation": "Property Consultation",
  "group-class": "Private Group Class",
  "virtual-consulting": "Virtual Consulting",
};

export default function InquiriesTable({ requests, refetch, onEdit }) {
  const update = async (id, patch) => {
    try { await api.patch(`/admin/service-requests/${id}`, patch); refetch(); }
    catch { toast.error("Update failed"); }
  };
  const exportCsv = () => {
    const headers = [
      "request_type", "name", "email", "phone", "status", "group_type",
      "group_size", "preferred_topic", "preferred_dates", "property_address",
      "property_size", "visit_length_hours", "add_ons", "message", "admin_notes",
      "created_at",
    ];
    const rows = requests.map((r) => [
      r.request_type, r.name, r.email, r.phone || "", r.status || "",
      r.group_type || "", r.group_size || "", r.preferred_topic || "",
      (r.preferred_dates || []).join(" | "), r.property_address || "",
      r.property_size || "", r.visit_length_hours || "",
      (r.add_ons || []).join(" | "), r.message || "", r.admin_notes || "",
      r.created_at,
    ]);
    downloadCsv("inquiries", headers, rows);
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={exportCsv}
          disabled={requests.length === 0}
          className="btn-pill btn-outline disabled:opacity-40"
          data-testid="admin-inquiries-export-csv"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>
      {requests.length === 0 && (
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-12 text-center text-gg-muted">
          No new inquiries. (Consultation, group class, and virtual consulting requests will appear here.)
        </div>
      )}
      {requests.map((r) => (
        <div key={r.id} className="rounded-2xl border border-gg-border bg-gg-surface p-5" data-testid={`inquiry-card-${r.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
                {INQUIRY_LABELS[r.request_type] || r.request_type}
              </p>
              <h3 className="font-heading text-xl text-[var(--gg-text)] mt-1">{r.name}</h3>
              <div className="text-xs text-gg-muted mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                <span className="inline-flex items-center gap-1"><Mail size={12} /> {r.email}</span>
                {r.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {r.phone}</span>}
                <span>· {new Date(r.created_at).toLocaleString("en-US")}</span>
              </div>
            </div>
            <select
              value={r.status}
              onChange={(e) => update(r.id, { status: e.target.value })}
              className="rounded-md border border-gg-border bg-gg-bg px-3 py-1.5 text-sm"
              data-testid={`inquiry-status-${r.id}`}
            >
              <option value="new">new</option>
              <option value="in-progress">in-progress</option>
              <option value="scheduled">scheduled</option>
              <option value="declined">declined</option>
              <option value="closed">closed</option>
            </select>
            <button
              type="button"
              onClick={() => onEdit?.(r)}
              className="text-xs px-3 py-1.5 rounded-full bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green inline-flex items-center gap-1"
              data-testid={`inquiry-edit-${r.id}`}
            >
              <Pencil size={12} /> Edit
            </button>
          </div>

          <div className="mt-3 grid sm:grid-cols-2 gap-2 text-sm text-[var(--gg-text)]/90">
            {r.request_type === "consultation" && (
              <>
                {r.property_address && <div><strong>Address:</strong> {r.property_address}</div>}
                {r.property_size && <div><strong>Size:</strong> {r.property_size}</div>}
                {r.consultation_hours && <div><strong>Hours:</strong> {r.consultation_hours} hr</div>}
                {(r.consultation_addons || []).length > 0 && <div><strong>Add-ons:</strong> {r.consultation_addons.join(", ")}</div>}
              </>
            )}
            {r.request_type === "group-class" && (
              <>
                {r.group_type && <div><strong>Group:</strong> {r.group_type}</div>}
                {r.group_size && <div><strong>Size:</strong> {r.group_size}</div>}
                {r.preferred_topic && <div className="sm:col-span-2"><strong>Topic:</strong> {r.preferred_topic}</div>}
                {r.preferred_dates && <div className="sm:col-span-2"><strong>Dates:</strong> {r.preferred_dates}</div>}
              </>
            )}
          </div>
          {r.message && (
            <div className="mt-3 rounded-lg bg-gg-bg border border-gg-border p-3 text-sm italic text-gg-muted">
              &quot;{r.message}&quot;
            </div>
          )}

          <details className="mt-3">
            <summary className="text-xs text-gg-brown font-semibold cursor-pointer uppercase tracking-[0.16em]">📝 Admin notes</summary>
            <Textarea
              defaultValue={r.admin_notes || ""}
              onBlur={(e) => {
                if (e.target.value !== (r.admin_notes || "")) {
                  update(r.id, { admin_notes: e.target.value });
                  toast.success("Notes saved");
                }
              }}
              rows={2}
              placeholder="Private notes (saved on blur)"
              className="mt-2 text-sm"
              data-testid={`inquiry-notes-${r.id}`}
            />
          </details>

          <div className="mt-3 flex gap-2 justify-end">
            <a
              href={`mailto:${r.email}?subject=Re:%20Your%20${encodeURIComponent(INQUIRY_LABELS[r.request_type] || "")}%20request`}
              className="text-xs px-3 py-1 rounded-full bg-gg-bg border border-gg-border hover:border-gg-green hover:text-gg-green"
            >
              Reply By Email
            </a>
            <button
              type="button"
              onClick={() => update(r.id, { archived: true })}
              className="text-xs px-3 py-1 rounded-full bg-gg-bg border border-gg-border text-gg-muted hover:text-[#9b6a26]"
              data-testid={`inquiry-archive-${r.id}`}
            >
              Archive
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
